/**
 * Walk-in poll panel - the host/presenter surface for polls
 * (docs/walk-in-polls.md). Pure DOM: no Babylon, no Photon. Network intent
 * goes through the handlers supplied by multiplayer.js via
 * registerPollHandlers (the registerBreakoutHandlers pattern); state and live
 * counts arrive through roomStore.
 *
 * Built with DOM nodes, stable shell, in-place count repaints - the setup
 * view's text inputs must never lose focus or value to a rebuild (the
 * breakoutPanel discipline).
 *
 * Three views:
 *  - Setup (no active poll): question input, 2-4 option label inputs,
 *    "Open poll" (disabled with a hint when the level has no zone anchors).
 *  - Live (poll open): question, per-option letter/label/live count, total,
 *    "Close poll & show results" (hinted to be done from inside the level).
 *  - Results (poll closed): frozen tallies with the winner highlighted,
 *    "Clear poll".
 *
 * All user-controlled text (question, labels) renders via textContent.
 */

import { CONFIG } from '../config/client.config.js';
import { TOKENS, createIcon } from './designSystem.js';
import { setPanelOpen, uiPanelsStore } from '../state/uiPanelsStore';
import { showToast } from '../utils/ui.js';
import { levelSupportsPolls } from '../config/pollZones.config.js';
import { computeWinners } from '../utils/pollProtocol';
import { X, Plus, Minus, Vote, Trophy, Eraser, Flag } from 'lucide';
import { roomStore } from '../state/roomStore';

const POLL = CONFIG.POLL;

/** Per-option accent colors - keep in sync with PollZoneManager's ACCENTS. */
const ACCENTS = ['#00c8ff', '#7c5cff', '#ff9d3c', '#3ddc84'];

let handlers = null; // supplied by multiplayer.js
let panel = null;
let overlay = null;
let bodyEl = null;
let subscriptions = [];
let view = null; // 'setup' | 'live' | 'results'
/** @type {import('../utils/pollProtocol').PollState|{active: false}|null} */
let state = { active: false };
/** @type {{pollId: string, counts: Record<string, number>, total: number}|null} */
let counts = null;
/** Live-view count elements, optionId -> span (in-place repaints). */
let countEls = {};
let totalEl = null;

// Setup-form state, module-level so a reopen keeps the host's draft.
let draftQuestion = '';
let draftCount = 2;
let draftLabels = ['', '', '', ''];

/**
 * @param {Object} h - handlers + providers from multiplayer.js
 * @param {() => string} h.getCurrentLevelId
 * @param {(opts: {question: string, optionLabels: string[]}) => Promise<any>} h.onOpen
 * @param {() => Promise<any>} h.onClose
 * @param {() => Promise<void>} h.onClear
 */
export function registerPollHandlers(h) {
  handlers = h;
  ensureSubscriptions();
}

function ensureSubscriptions() {
  if (subscriptions.length) return;
  subscriptions.push(
    roomStore.subscribe(
      (s) => s.poll,
      (next) => {
        state = next || { active: false };
        if (isPanelOpen()) renderForState();
      },
      { fireImmediately: true },
    ),
    roomStore.subscribe(
      (s) => s.pollCounts,
      (next) => {
        counts = next;
        if (isPanelOpen() && view === 'live') repaintCounts();
      },
      { fireImmediately: true },
    ),
    uiPanelsStore.subscribe(
      (s) => s.open.poll,
      (visible) => {
        if (visible) {
          if (!handlers) return;
          state = roomStore.getState().poll || { active: false };
          counts = roomStore.getState().pollCounts;
          ensurePanel();
          overlay.style.display = 'flex';
          view = null;
          renderForState();
        } else if (overlay) {
          overlay.style.display = 'none';
        }
      },
      { fireImmediately: true },
    ),
  );
}

/** Host action bar button hook (multiplayerUI.js). */
export function togglePollPanel() {
  if (isPanelOpen()) closePanel();
  else openPanel();
}

function isPanelOpen() {
  return uiPanelsStore.getState().open.poll;
}

function openPanel() {
  if (!handlers) return;
  ensurePanel();
  setPanelOpen('poll', true);
  document.exitPointerLock?.();
}

function closePanel() {
  setPanelOpen('poll', false);
}

export function disposePollPanel() {
  closePanel();
  subscriptions.forEach((unsubscribe) => unsubscribe());
  subscriptions = [];
  handlers = null;
}

export function resetPollPanelDrafts() {
  draftQuestion = '';
  draftCount = 2;
  draftLabels = ['', '', '', ''];
  view = null;
}

function ensurePanel() {
  if (panel) return;
  // Full-screen scrim that dims the world and centers the modal card. Clicking
  // the backdrop (never the card) closes the panel; Esc is intentionally left
  // to the settings menu (hard constraint), so dismissal is backdrop + X only.
  overlay = document.createElement('div');
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(2px)',
    zIndex: String(TOKENS.z.modal),
    pointerEvents: 'auto',
    padding: '16px',
  });
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closePanel();
  });

  panel = document.createElement('div');
  panel.id = 'poll-panel';
  Object.assign(panel.style, {
    width: '320px',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'calc(100vh - 140px)',
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(18,18,22,0.94)',
    color: TOKENS.text.onDark,
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '12px',
    boxShadow: TOKENS.shadow.md,
    backdropFilter: 'blur(4px)',
    fontFamily: TOKENS.font,
    fontSize: '13px',
    pointerEvents: 'auto',
    overflow: 'hidden',
  });
  // Clicks inside the card must never reach the backdrop's close handler.
  panel.addEventListener('click', (e) => e.stopPropagation());

  const header = document.createElement('div');
  Object.assign(header.style, {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    flex: 'none',
  });
  const title = document.createElement('div');
  Object.assign(title.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: TOKENS.weight.semibold,
  });
  title.appendChild(createIcon(Vote, { size: 16 }));
  const titleText = document.createElement('span');
  titleText.textContent = 'Walk-in poll';
  title.appendChild(titleText);
  const closeBtn = iconButton(X, 'Close');
  closeBtn.onclick = () => closePanel();
  header.append(title, closeBtn);

  bodyEl = document.createElement('div');
  Object.assign(bodyEl.style, {
    flex: '1',
    overflowY: 'auto',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  });
  // Panel inputs must never drive the avatar (WASD keys while typing).
  bodyEl.addEventListener('keydown', (e) => e.stopPropagation());
  bodyEl.addEventListener('keyup', (e) => e.stopPropagation());

  panel.append(header, bodyEl);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
}

function renderForState() {
  const nextView = !state || !state.active ? 'setup' : state.status === 'open' ? 'live' : 'results';
  if (nextView !== view) {
    view = nextView;
    if (view === 'setup') buildSetupView();
    else if (view === 'live') buildLiveView();
    else buildResultsView();
    return;
  }
  if (view === 'live') repaintCounts();
}

// --- Shared small builders (the breakoutPanel set) ---

function iconButton(icon, titleText) {
  const btn = document.createElement('button');
  btn.title = titleText;
  Object.assign(btn.style, {
    background: 'none',
    border: 'none',
    color: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px',
  });
  btn.appendChild(createIcon(icon, { size: 16 }));
  return btn;
}

function actionButton(label, { primary = false, danger = false, icon = null } = {}) {
  const btn = document.createElement('button');
  Object.assign(btn.style, {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    border: 'none',
    borderRadius: TOKENS.radius.md,
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: TOKENS.weight.medium,
    cursor: 'pointer',
    color: '#fff',
    background: danger
      ? 'rgba(244, 67, 54, 0.85)'
      : primary
        ? TOKENS.action.primary.background
        : 'rgba(255,255,255,0.12)',
  });
  if (icon) btn.appendChild(createIcon(icon, { size: 14 }));
  const span = document.createElement('span');
  span.textContent = label;
  btn.appendChild(span);
  return btn;
}

function sectionLabel(text) {
  const el = document.createElement('div');
  el.textContent = text;
  Object.assign(el.style, {
    color: TOKENS.text.muted,
    fontSize: '11px',
    fontWeight: TOKENS.weight.medium,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  });
  return el;
}

function textInput(value, placeholder = '', maxLength = POLL.MAX_OPTION_LABEL_LENGTH) {
  const input = document.createElement('input');
  input.type = 'text';
  input.value = value;
  input.placeholder = placeholder;
  input.maxLength = maxLength;
  Object.assign(input.style, {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: TOKENS.radius.md,
    color: '#fff',
    padding: '6px 10px',
    fontFamily: 'inherit',
    fontSize: '13px',
    width: '100%',
    boxSizing: 'border-box',
  });
  return input;
}

function hintText(text) {
  const el = document.createElement('div');
  el.textContent = text;
  Object.assign(el.style, { color: TOKENS.text.muted, fontSize: '12px' });
  return el;
}

function letterChip(optionId, index) {
  const chip = document.createElement('span');
  chip.textContent = optionId.toUpperCase();
  Object.assign(chip.style, {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: ACCENTS[index % ACCENTS.length],
    color: '#0b0e18',
    fontWeight: TOKENS.weight.semibold,
    fontSize: '12px',
    flex: 'none',
  });
  return chip;
}

// --- Setup view ---

function buildSetupView() {
  bodyEl.replaceChildren();
  countEls = {};
  totalEl = null;

  bodyEl.appendChild(sectionLabel('Question'));
  const questionInput = textInput(
    draftQuestion,
    'What should we vote on?',
    POLL.MAX_QUESTION_LENGTH,
  );
  questionInput.oninput = () => {
    draftQuestion = questionInput.value;
  };
  bodyEl.appendChild(questionInput);

  // Option count + label inputs.
  const countRow = document.createElement('div');
  Object.assign(countRow.style, { display: 'flex', alignItems: 'center', gap: '10px' });
  countRow.appendChild(sectionLabel('Options'));
  const minusBtn = iconButton(Minus, 'Fewer options');
  const countText = document.createElement('span');
  countText.textContent = String(draftCount);
  Object.assign(countText.style, { fontWeight: TOKENS.weight.semibold, minWidth: '14px' });
  const plusBtn = iconButton(Plus, 'More options');
  countRow.append(minusBtn, countText, plusBtn);
  bodyEl.appendChild(countRow);

  const optionsWrap = document.createElement('div');
  Object.assign(optionsWrap.style, { display: 'flex', flexDirection: 'column', gap: '8px' });
  bodyEl.appendChild(optionsWrap);

  const rebuildOptionRows = () => {
    optionsWrap.replaceChildren();
    for (let i = 0; i < draftCount; i++) {
      const row = document.createElement('div');
      Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: '8px' });
      row.appendChild(letterChip('abcd'[i], i));
      const input = textInput(draftLabels[i], `Option ${'ABCD'[i]}`);
      input.oninput = () => {
        draftLabels[i] = input.value;
      };
      row.appendChild(input);
      optionsWrap.appendChild(row);
    }
    countText.textContent = String(draftCount);
  };
  minusBtn.onclick = () => {
    if (draftCount > POLL.MIN_OPTIONS) {
      draftCount--;
      rebuildOptionRows();
    }
  };
  plusBtn.onclick = () => {
    if (draftCount < POLL.MAX_OPTIONS) {
      draftCount++;
      rebuildOptionRows();
    }
  };
  rebuildOptionRows();

  const supported = levelSupportsPolls(handlers.getCurrentLevelId());
  const openBtn = actionButton('Open poll', { primary: true, icon: Vote });
  openBtn.disabled = !supported;
  if (!supported) openBtn.style.opacity = '0.5';
  openBtn.onclick = async () => {
    if (!draftQuestion.trim()) {
      showToast('Enter a question first');
      questionInput.focus();
      return;
    }
    openBtn.disabled = true;
    try {
      await handlers.onOpen({
        question: draftQuestion,
        optionLabels: draftLabels.slice(0, draftCount),
      });
      showToast('Poll opened - zones are on the floor');
    } catch (err) {
      showToast(`Could not open the poll: ${err.message}`);
    } finally {
      openBtn.disabled = false;
    }
  };
  bodyEl.appendChild(openBtn);
  if (!supported) {
    bodyEl.appendChild(
      hintText('This level has no poll zones - open the poll from the exhibition hall.'),
    );
  } else {
    bodyEl.appendChild(
      hintText(
        'Lettered zones appear on the floor in front of the theater stage. People vote by standing in one.',
      ),
    );
  }
}

// --- Live view ---

function buildLiveView() {
  if (!state || !state.active) return;
  bodyEl.replaceChildren();
  countEls = {};

  const question = document.createElement('div');
  question.textContent = state.question;
  Object.assign(question.style, { fontWeight: TOKENS.weight.semibold, fontSize: '14px' });
  bodyEl.appendChild(question);

  const list = document.createElement('div');
  Object.assign(list.style, { display: 'flex', flexDirection: 'column', gap: '8px' });
  state.options.forEach((option, i) => {
    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      background: 'rgba(255,255,255,0.06)',
      borderRadius: TOKENS.radius.md,
      padding: '8px 10px',
    });
    row.appendChild(letterChip(option.id, i));
    const label = document.createElement('span');
    label.textContent = option.label;
    Object.assign(label.style, { flex: '1', overflow: 'hidden', textOverflow: 'ellipsis' });
    const count = document.createElement('span');
    count.textContent = '—';
    Object.assign(count.style, { fontWeight: TOKENS.weight.semibold, fontSize: '15px' });
    countEls[option.id] = count;
    row.append(label, count);
    list.appendChild(row);
  });
  bodyEl.appendChild(list);

  totalEl = document.createElement('div');
  Object.assign(totalEl.style, { color: TOKENS.text.muted, fontSize: '12px' });
  bodyEl.appendChild(totalEl);

  const closeBtn = actionButton('Close poll & show results', { primary: true, icon: Flag });
  closeBtn.onclick = async () => {
    closeBtn.disabled = true;
    try {
      await handlers.onClose();
    } catch (err) {
      showToast(`Could not close the poll: ${err.message}`);
      closeBtn.disabled = false;
    }
  };
  bodyEl.appendChild(closeBtn);
  // The closer's local view becomes the frozen result, and counts only exist
  // where the zones do.
  if (handlers.getCurrentLevelId() !== state.levelId) {
    bodyEl.appendChild(
      hintText(
        'You are in a different level - go to the poll level to see live counts and close it there.',
      ),
    );
  }
  repaintCounts();
}

function repaintCounts() {
  if (view !== 'live' || !state || !state.active) return;
  const live = counts && counts.pollId === state.pollId ? counts : null;
  for (const [optionId, el] of Object.entries(countEls)) {
    el.textContent = live ? String(live.counts[optionId] ?? 0) : '—';
  }
  if (totalEl) {
    totalEl.textContent = live
      ? `${live.total} ${live.total === 1 ? 'person is' : 'people are'} standing in a zone`
      : 'Stand in the poll level to see live counts.';
  }
}

// --- Results view ---

function buildResultsView() {
  if (!state || !state.active) return;
  // Captured so the forEach closure below keeps the narrowed type.
  const result = state;
  bodyEl.replaceChildren();
  countEls = {};
  totalEl = null;

  const question = document.createElement('div');
  question.textContent = result.question;
  Object.assign(question.style, { fontWeight: TOKENS.weight.semibold, fontSize: '14px' });
  bodyEl.appendChild(question);

  const winners = new Set(computeWinners(result.closedTallies));
  const list = document.createElement('div');
  Object.assign(list.style, { display: 'flex', flexDirection: 'column', gap: '8px' });
  result.options.forEach((option, i) => {
    const won = winners.has(option.id);
    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      background: won ? 'rgba(61, 220, 132, 0.15)' : 'rgba(255,255,255,0.06)',
      border: won ? '1px solid rgba(61, 220, 132, 0.5)' : '1px solid transparent',
      borderRadius: TOKENS.radius.md,
      padding: '8px 10px',
    });
    row.appendChild(letterChip(option.id, i));
    const label = document.createElement('span');
    label.textContent = option.label;
    Object.assign(label.style, { flex: '1', overflow: 'hidden', textOverflow: 'ellipsis' });
    row.appendChild(label);
    if (won) row.appendChild(createIcon(Trophy, { size: 14, color: '#3ddc84' }));
    const count = document.createElement('span');
    count.textContent = String(result.closedTallies?.[option.id] ?? 0);
    Object.assign(count.style, { fontWeight: TOKENS.weight.semibold, fontSize: '15px' });
    row.appendChild(count);
    list.appendChild(row);
  });
  bodyEl.appendChild(list);

  const total = document.createElement('div');
  total.textContent = `${result.totalVoters ?? 0} total ${result.totalVoters === 1 ? 'vote' : 'votes'}`;
  Object.assign(total.style, { color: TOKENS.text.muted, fontSize: '12px' });
  bodyEl.appendChild(total);

  const clearBtn = actionButton('Clear poll', { icon: Eraser });
  clearBtn.onclick = async () => {
    clearBtn.disabled = true;
    try {
      await handlers.onClear();
    } catch (err) {
      showToast(`Could not clear the poll: ${err.message}`);
      clearBtn.disabled = false;
    }
  };
  bodyEl.appendChild(clearBtn);
  bodyEl.appendChild(hintText('Results stay on the floor for everyone until you clear the poll.'));
}
